import { ArrowLeft, ArrowRight, BedDouble, Check, ChevronLeft, ChevronRight, CircleAlert, CreditCard, Eye, Images, MapPin, Minus, Plane, Plus, Share2, Utensils, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { photoBackground, RegulatedNote } from "../../components/Cards";
import { Modal } from "../../components/Modal";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { useGuest } from "../../components/shell/GuestShell";
import { Spinner } from "../../components/Spinner";
import { routeSummary, stayLabel } from "../../lib/trips";
import { PlanningModal } from "../../components/trips/PlanningModal";
import { SaveTripButton } from "../../components/trips/SaveTripButton";
import { track } from "../../lib/analytics";
import { setCanonical, websiteTripUrl } from "../../lib/website";
import { ReservationModal } from "../../components/trips/ReservationModal";
import { TripGoalDialog } from "../../components/trips/TripGoalDialog";
import { formatCalendarDate } from "../../lib/format";
import { useLibreWallet, useLoanConfig, useReservationConfig, useTripPage } from "../../lib/queries";
import { OCCUPANCY_INFO, amountWithCurrency, departureLabel, mxn, rateFor, upcomingDepartures } from "../../lib/reservations";
import { planTrip, pricedOccupancies, type PlanChoice } from "@aventuria/client-core/trip-planner";
import type { Occupancy, ReservationRequest, TripContent, TripPage as TripPageData } from "../../types";
import { useOpenCustomTrip } from "../../components/trips/CustomTripPrompt";
import { mediaVariant, slotBackground, slotSrcSet } from "../../lib/media";

const HERO_SHADE = "linear-gradient(180deg, rgba(0,37,87,0.15) 0%, rgba(0,37,87,0.35) 45%, rgba(0,37,87,0.94) 100%)";
const OCCUPANCY_CHOICES: Occupancy[] = ["double", "triple", "single"];
const MONTHS = new Intl.DateTimeFormat("es-MX", { month: "long", year: "numeric" });

/** The trip page (E4 template, designs 1a/2a): editorial itinerary with a sticky price panel. */
export function TripPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const preview = searchParams.get("preview") === "1" && user?.role === "admin";
  const trip = useTripPage(slug, preview);
  const navigate = useNavigate();
  // The indexable page of a published trip is the website's; the app page defers to it.
  const canonicalSlug = trip.data && !trip.data.preview ? trip.data.slug : null;
  useEffect(() => (canonicalSlug ? setCanonical(websiteTripUrl(canonicalSlug)) : undefined), [canonicalSlug]);
  usePageHeader(trip.data?.name ?? "Viaje", trip.data ? [trip.data.countries[0], trip.data.durationDays ? `${trip.data.durationDays} días` : null].filter(Boolean).join(" · ") : undefined);

  if (trip.isLoading) return <div className="page-state"><Spinner label="Cargando viaje" /><span>Cargando viaje</span></div>;
  if (trip.isError || !trip.data) {
    return (
      <div className="page-state">
        <CircleAlert aria-hidden="true" />
        <strong>Este viaje no está disponible</strong>
        <span>Puede que ya no esté publicado o que la dirección haya cambiado.</span>
        <button type="button" className="secondary-button" onClick={() => navigate("/explorar")}>Volver a Viajes</button>
      </div>
    );
  }
  return <TripView trip={trip.data} />;
}

function TripView({ trip }: { trip: TripPageData }) {
  const navigate = useNavigate();
  const location = useLocation();
  const content = trip.content;
  // A guest reads the whole page; reserving, apartar and the advisor ask for an account first.
  const { guest, askForAccount } = useGuest();
  const config = useReservationConfig();
  const loans = useLoanConfig({ enabled: !guest });
  const { wallet } = useLibreWallet({ enabled: !guest });
  const departures = useMemo(() => upcomingDepartures(content, trip.earliestDeparture), [content, trip.earliestDeparture]);
  const [pickedOccupancy, setOccupancy] = useState<Occupancy>("double");
  // Seasons often share dates and differ by hotel category or package: the customer's picks per
  // label part ("Primera · Terrestre"); the season itself follows the departure when it can.
  const [picks, setPicks] = useState<Record<number, string>>({});
  const [lastPicked, setLastPicked] = useState<number | undefined>(undefined);
  const [departure, setDeparture] = useState<string | null>(departures[0] ?? null);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveEntry, setReserveEntry] = useState<"trip_page" | "planning_modal" | "mobile_bar">("trip_page");
  const [planOpen, setPlanOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [openDays, setOpenDays] = useState<Set<number>>(() => new Set([content.itinerary[0]?.day_number ?? 1]));
  const [openPanels, setOpenPanels] = useState<Set<string>>(new Set());
  const [shared, setShared] = useState(false);
  const [photoIndex, setPhotoIndex] = useState<number | null>(null);
  const [stuck, setStuck] = useState(false);
  const heroRef = useRef<HTMLElement>(null);
  const gallery = trip.gallery ?? [];
  const route = useMemo(() => routeSummary(content.itinerary), [content.itinerary]);

  // Design 2a: once the hero scrolls away, a slim bar keeps the name, the price and "Ver salidas" in view.
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setStuck(Boolean(entry && !entry.isIntersecting && entry.boundingClientRect.top < 0)), { threshold: 0 });
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (photoIndex === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") setPhotoIndex((current) => (current === null ? null : (current + 1) % gallery.length));
      if (event.key === "ArrowLeft") setPhotoIndex((current) => (current === null ? null : (current - 1 + gallery.length) % gallery.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photoIndex, gallery.length]);
  const revealPanel = (key: string) => {
    setOpenPanels((current) => new Set(current).add(key));
    window.setTimeout(() => document.getElementById(`panel-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
  };
  const showDepartures = () => revealPanel("departures");

  const plan = useMemo(() => planTrip(content, departure, picks, lastPicked), [content, departure, picks, lastPicked]);
  const occupancies = pricedOccupancies(plan.season, OCCUPANCY_CHOICES);
  // A room type the chosen row has no price for falls back to the first it has (never a silent double rate).
  const occupancy = occupancies.includes(pickedOccupancy) ? pickedOccupancy : occupancies[0]!;
  const rate = rateFor(content, occupancy, departure, plan.category);
  const pick = (index: number, value: string) => { setPicks((current) => ({ ...current, [index]: value })); setLastPicked(index); };
  const extraAddOns = new Set(content.pricing.add_ons.filter((addOn) => !(addOn.departure_dates ?? []).length).map((addOn) => addOn.name.trim().toLowerCase())).size;
  // A trip without a requestable departure turns into a tailor-made request about this trip.
  const askCustomTrip = useOpenCustomTrip("trip_page_no_departures", {
    destinations: content.product.countries.length ? content.product.countries : [trip.name],
    note: `Me interesa «${trip.name}»${content.product.route_display ? ` (${content.product.route_display})` : ""}, en otras fechas.`
  });
  const minPerPassenger = config.data?.apartadoMinPerPassengerMxn ?? 9000;
  const suggestedGoal = useMemo(() => {
    const from = rateFor(content, "double", departure);
    if (!from) return null;
    const pax = Math.max(1, content.product.minimum_passengers ?? 2);
    const fx = config.data?.usdToMxnRate ?? 20;
    return Math.round(from.amount * pax * (from.currency.toUpperCase() === "USD" ? fx : 1));
  }, [content, departure, config.data]);

  const meals = useMemo(() => {
    const counts = { Desayuno: 0, Almuerzo: 0, Cena: 0 };
    for (const day of content.itinerary) for (const meal of day.meals) if (meal in counts) counts[meal as keyof typeof counts] += 1;
    return counts;
  }, [content]);
  const hasFlight = content.itinerary.some((day) => day.transport.some((leg) => leg.type === "vuelo")) || content.included.some((item) => /vuelo interno/i.test(item.description));
  const allOpen = content.itinerary.every((day) => openDays.has(day.day_number ?? 0));
  const toggleDay = (number: number) => setOpenDays((current) => { const next = new Set(current); if (next.has(number)) next.delete(number); else next.add(number); return next; });
  const togglePanel = (key: string) => setOpenPanels((current) => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; });

  // "Ver qué incluye" on Solicitud recibida links to /viajes/:slug#incluye: open that panel and scroll to it.
  useEffect(() => {
    if (location.hash !== "#incluye") return;
    setOpenPanels((current) => new Set(current).add("included"));
    const timer = window.setTimeout(() => document.getElementById("panel-included")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  // The account prompt brings a guest back with ?intent=, so the action they chose reopens by itself.
  const intentPath = (intent: "apartar" | "reservar") => `${location.pathname}?intent=${intent}`;
  const openPlan = () => {
    if (guest) { askForAccount("apartar este viaje", intentPath("apartar")); return; }
    setPlanOpen(true);
  };
  const openReserve = (entry: "trip_page" | "planning_modal" | "mobile_bar") => {
    if (guest) { askForAccount("reservar este viaje", intentPath("reservar")); return; }
    track("trip_reserve_clicked", { trip_id: trip.id, departure_context: departure, rate_context: rate ? `${rate.amount} ${rate.currency} ${occupancy}` : null });
    setReserveEntry(entry);
    setReserveOpen(true);
  };

  // "Reservar" / "Apartar con mi Wallet" on the website, or the same buttons pressed as a guest before
  // signing up, arrive as ?intent=: open that action once, then drop the parameter.
  const [searchParams, setSearchParams] = useSearchParams();
  const intent = searchParams.get("intent");
  useEffect(() => {
    if (intent !== "apartar" && intent !== "reservar") return;
    if (intent === "apartar") openPlan();
    else if (departures.length) openReserve("trip_page");
    if (!guest) setSearchParams((current) => { const next = new URLSearchParams(current); next.delete("intent"); return next; }, { replace: true });
    // Runs once per arrival; the handlers read the latest state when called.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, guest]);

  const share = async () => {
    const url = window.location.href.replace(/\?preview=1$/, "");
    try {
      if (navigator.share) await navigator.share({ title: trip.name, text: content.product.summary, url });
      else { await navigator.clipboard.writeText(url); setShared(true); setTimeout(() => setShared(false), 1600); }
    } catch { /* cancelled */ }
  };

  const submitted = (request: ReservationRequest) => {
    setReserveOpen(false); setPlanOpen(false);
    navigate(`/solicitudes/${request.id}`, { state: { justSubmitted: true } });
  };

  const departureMonths = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const date of departures) {
      const [year, month] = date.split("-");
      const key = `${year}-${month}`;
      groups.set(key, [...(groups.get(key) ?? []), date]);
    }
    return [...groups.entries()].map(([key, dates]) => ({ key, label: MONTHS.format(new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, 1)), days: dates.map((date) => Number(date.slice(8, 10))) }));
  }, [departures]);

  return (
    <div className="page-container trip-page">
      {trip.preview && (
        <div className="preview-banner" role="status"><Eye size={16} aria-hidden="true" /> Vista previa del borrador · así se verá la página al publicarse. <button type="button" className="link-button" onClick={() => navigate(`/admin/viajes/${trip.id}`)}>Volver al editor</button></div>
      )}
      <nav className="trip-breadcrumb" aria-label="Ruta de navegación">
        <button type="button" className="back-button" onClick={() => navigate(trip.region ? `/explorar/${trip.region.slug}` : "/explorar")}><ArrowLeft size={16} aria-hidden="true" /> {trip.region ? trip.region.name : "Viajes"}</button>
        <span className="crumbs"><Link to="/explorar">Viajes</Link>{trip.region && <> <span aria-hidden="true">→</span> <Link to={`/explorar/${trip.region.slug}`}>{trip.region.name}</Link></>}{trip.destination && <> <span aria-hidden="true">→</span> <Link to={`/explorar/${trip.region?.slug ?? ""}`}>{trip.destination.name}</Link></>} <span aria-hidden="true">→</span> <span aria-current="page">{trip.name}</span></span>
      </nav>

      <div className={`trip-sticky-bar${stuck ? " show" : ""}`} aria-hidden={!stuck}>
        <div className="trip-sticky-copy">
          <strong>{trip.name}</strong>
          <span>{[trip.durationDays ? `${trip.durationDays} días` : null, trip.routeDisplay || trip.cities.slice(0, 4).join(" · ")].filter(Boolean).join(" · ")}</span>
        </div>
        <div className="trip-sticky-price" translate="no">
          <small>Desde · por persona</small>
          <strong>{rate ? amountWithCurrency(rate.amount, rate.currency) : "Consultar"}</strong>
        </div>
        <div className="trip-sticky-actions">
          {departures.length > 0 && <button type="button" className="secondary-button small" onClick={showDepartures} tabIndex={stuck ? 0 : -1}>Ver salidas</button>}
          <button type="button" className="primary-button solid small" onClick={() => openReserve("trip_page")} disabled={!departures.length} tabIndex={stuck ? 0 : -1}>Reservar</button>
        </div>
      </div>

      <section className="trip-hero" style={photoBackground(trip.heroImageUrl, HERO_SHADE, "wide")} ref={heroRef}>
        <div className="trip-hero-top">
          {gallery.length > 0 && <button type="button" className="glass-button" onClick={() => setPhotoIndex(0)}><Images size={14} aria-hidden="true" /> <span translate="no">{gallery.length} {gallery.length === 1 ? "foto" : "fotos"}</span></button>}
          <SaveTripButton trip={trip} placement="trip_page" variant="glass" />
          <button type="button" className="glass-button" onClick={() => void share()}>{shared ? <><Check size={14} aria-hidden="true" /> Enlace copiado</> : <><Share2 size={14} aria-hidden="true" /> Compartir</>}</button>
        </div>
        <div className="trip-hero-body">
          <span className="hero-eyebrow">{[trip.destination?.country ?? trip.countries[0] ?? trip.cities[0], trip.durationDays ? `${trip.durationDays} días${trip.durationNights ? ` / ${trip.durationNights} noches` : ""}` : null, content.product.service_type || null].filter(Boolean).join(" · ")}</span>
          <h2>{trip.name}</h2>
          <div className="trip-chips">
            {trip.routeDisplay && <span className="glass-pill"><MapPin size={12} aria-hidden="true" /> {trip.routeDisplay}</span>}
            {trip.highlights.filter((item) => item !== content.product.service_type).map((item) => <span key={item} className="glass-pill">{item}</span>)}
          </div>
        </div>
      </section>

      <div className="trip-grid">
        <div className="trip-main">
          {content.product.summary && <p className="trip-summary">{content.product.summary}</p>}
          <div className="trip-stats">
            <Stat label="Llegadas" value={trip.arrivalDays.join(" y ") || (departures.length ? "Fechas fijas" : "Consultar")} />
            <Stat label="Ciudades" value={String(new Set(trip.cities).size || content.itinerary.length)} />
            <Stat label="Comidas" value={[meals.Desayuno ? `${meals.Desayuno} des` : null, meals.Almuerzo ? `${meals.Almuerzo} alm` : null, meals.Cena ? `${meals.Cena} cen` : null].filter(Boolean).join(" · ") || "Según itinerario"} />
            <Stat label="Vuelo interno" value={hasFlight ? "Incluido" : "No aplica"} />
          </div>

          {gallery.length > 0 && (
            <section className="trip-gallery" aria-label="Galería de fotos">
              {gallery.slice(0, 5).map((photo, index) => (
                <button key={`${photo.url}-${index}`} type="button" className={`gallery-tile${index === 0 ? " lead" : ""}`} style={{ backgroundImage: slotBackground(photo.url, index === 0 ? "half" : "card") }} onClick={() => setPhotoIndex(index)} aria-label={photo.title ? `Ver foto: ${photo.title}` : `Ver foto ${index + 1}`}>
                  {photo.title && <span className="gallery-tile-caption">{photo.title}</span>}
                  {index === 4 && gallery.length > 5 && <span className="gallery-more" translate="no">+{gallery.length - 5}</span>}
                </button>
              ))}
            </section>
          )}

          {route.length > 1 && (
            <section className="route-strip" aria-label="Ruta">
              <span className="rail-title section-eyebrow" style={{ margin: 0 }}>Ruta</span>
              <ol className="route-list">
                {route.map((stay, index) => (
                  <li key={`${stay.city}-${stay.firstDay}`}>
                    <span className="route-dot" aria-hidden="true" />
                    <div><strong>{stay.city}</strong><span translate="no">{stayLabel(stay)}</span></div>
                    {index < route.length - 1 && <ArrowRight size={14} className="route-arrow" aria-hidden="true" />}
                  </li>
                ))}
              </ol>
            </section>
          )}

          <section className="trip-section" aria-labelledby="itinerary-title">
            <div className="section-heading small">
              <span className="rail-title section-eyebrow" id="itinerary-title" style={{ margin: 0 }}>Itinerario día por día</span>
              <button type="button" className="link-button" onClick={() => setOpenDays(allOpen ? new Set() : new Set(content.itinerary.map((day) => day.day_number ?? 0)))}>{allOpen ? "Contraer todos" : "Expandir todos"}</button>
            </div>
            <ol className="day-list">
              {content.itinerary.map((day, index) => {
                const number = day.day_number ?? index + 1;
                const open = openDays.has(number);
                return (
                  <li key={`${number}-${index}`} className={`day-item${open ? " open" : ""}`}>
                    <button type="button" className="day-head" onClick={() => toggleDay(number)} aria-expanded={open} aria-controls={`day-${number}`}>
                      <span className="day-number"><small>DÍA</small><strong>{number}</strong></span>
                      <span className="day-copy">
                        <strong>{day.title || day.cities.join(" – ") || `Día ${number}`}</strong>
                        <span>{[day.cities.join(" · ") || null, day.overnight.city ? `Noche en ${day.overnight.city}` : null].filter(Boolean).join(" · ")}</span>
                      </span>
                      <span className="day-sign" aria-hidden="true">{open ? <Minus size={16} /> : <Plus size={16} />}</span>
                    </button>
                    {open && (
                      <div className="day-body" id={`day-${number}`}>
                        {day.description.split("\n").filter(Boolean).map((paragraph, i) => <p key={i}>{paragraph}</p>)}
                        <div className="day-tags">
                          {day.meals.map((meal) => <span key={meal} className="tag"><Utensils size={10} aria-hidden="true" /> {meal}</span>)}
                          {day.transport.map((leg, i) => <span key={i} className="tag">{leg.type === "vuelo" ? <Plane size={10} aria-hidden="true" /> : null} {leg.type}{leg.duration_text ? ` · ${leg.duration_text}` : ""}</span>)}
                          {day.overnight.hotel_ref && <span className="tag"><BedDouble size={10} aria-hidden="true" /> {day.overnight.hotel_ref}</span>}
                        </div>
                        {day.notes.map((note) => <p key={note} className="day-note"><strong>Nota</strong> {note}</p>)}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </section>

          <section className="trip-section" aria-labelledby="detail-title">
            <span className="rail-title section-eyebrow" id="detail-title">El detalle fino</span>
            <div className="detail-panels">
              {content.accommodations.length > 0 && (
                <Panel id="hotels" title="Hoteles previstos" hint={`${content.accommodations.length} etapas${content.accommodations[0]?.category ? ` · categoría ${content.accommodations[0].category}` : ""}`} open={openPanels.has("hotels")} onToggle={() => togglePanel("hotels")}>
                  <div className="table-wrap">
                    <table className="trip-table">
                      <thead><tr><th>Noches</th><th>Ciudad</th><th>Hotel o similar</th><th>Cat.</th></tr></thead>
                      <tbody>{content.accommodations.map((stay, i) => <tr key={i}><td translate="no">{stay.nights ?? "—"}</td><td>{stay.city}</td><td>{stay.hotel_options.join(" · ")}</td><td>{stay.category}</td></tr>)}</tbody>
                    </table>
                  </div>
                </Panel>
              )}
              <Panel id="included" title="Qué incluye" hint={`${content.included.length} inclusiones · ${content.excluded.length} exclusiones`} open={openPanels.has("included")} onToggle={() => togglePanel("included")}>
                <div className="incl-grid">
                  <div><span className="field-label">Incluye</span><ul className="check-list">{content.included.map((item, i) => <li key={i}><Check size={14} aria-hidden="true" /> {item.description}</li>)}</ul></div>
                  <div><span className="field-label">No incluye</span><ul className="check-list muted">{content.excluded.map((item, i) => <li key={i}><X size={14} aria-hidden="true" /> {item.description}</li>)}</ul></div>
                </div>
              </Panel>
              {content.pricing.seasons.length > 0 && (
                <Panel id="prices" title="Tarifas por temporada y suplementos" hint={`${content.pricing.seasons.length} temporadas · ${content.pricing.add_ons.length} add-ons`} open={openPanels.has("prices")} onToggle={() => togglePanel("prices")}>
                  <RatesTable content={content} />
                  {content.pricing.add_ons.length > 0 && (
                    <ul className="addon-list">{content.pricing.add_ons.map((addon, i) => <li key={i}><span>{addon.name}</span><strong translate="no">{addon.amount !== null ? amountWithCurrency(addon.amount, addon.currency || content.pricing.currency) : "Consultar"}</strong></li>)}</ul>
                  )}
                  {content.pricing.conditions.map((condition, i) => <p key={i} className="fine-print">{condition}</p>)}
                </Panel>
              )}
              {departures.length > 0 && (
                <Panel id="departures" title="Calendario de salidas" hint={`${trip.arrivalDays.join(" y ") || "Fechas"} · ${departures.length} salidas`} open={openPanels.has("departures")} onToggle={() => togglePanel("departures")}>
                  <div className="departure-grid">
                    {departureMonths.map((month) => (
                      <div key={month.key} className="departure-month">
                        <strong>{month.label.charAt(0).toUpperCase() + month.label.slice(1)}</strong>
                        <div className="departure-days">{month.days.map((day) => {
                          const iso = `${month.key}-${String(day).padStart(2, "0")}`;
                          return <button key={iso} type="button" className={`departure-day${departure === iso ? " active" : ""}`} onClick={() => setDeparture(iso)} aria-pressed={departure === iso} translate="no">{day}</button>;
                        })}</div>
                      </div>
                    ))}
                  </div>
                  <p className="fine-print">Las salidas se solicitan con al menos {config.data?.minLeadDays ?? 28} días de anticipación: se muestran a partir del {formatCalendarDate(trip.earliestDeparture, "long")}.{trip.validUntil ? ` Vigencia de tarifas hasta ${formatCalendarDate(trip.validUntil, "long")}.` : ""}</p>
                </Panel>
              )}
              {content.important_notes.length > 0 && (
                <Panel id="notes" title="Notas importantes" hint={[...new Set(content.important_notes.map((note) => note.category))].slice(0, 4).join(", ")} open={openPanels.has("notes")} onToggle={() => togglePanel("notes")}>
                  <ul className="notes-list">{content.important_notes.map((note, i) => <li key={i}><span className="tag">{note.category}</span><p>{note.text}</p></li>)}</ul>
                </Panel>
              )}
            </div>
          </section>

          <section className="trip-cta-cards">
            <div className="trip-cta-card">
              <span className="page-eyebrow">Viajar nunca había estado tan al alcance</span>
              <h3>Abre un Apartado para este viaje</h3>
              <p>Aparta tu salida con <span translate="no">{mxn(minPerPassenger).replace(".00", "")}</span> por pasajero desde tu Wallet Libre.</p>
              <button type="button" className="secondary-button" onClick={openPlan}>Crear Apartado para este viaje <ArrowRight size={15} aria-hidden="true" /></button>
            </div>
            {loans.data?.enabled && (
              <div className="trip-cta-card dark">
                <span className="page-eyebrow">Financiamiento</span>
                <h3>Viaja ahora, paga después</h3>
                <p>Te ayudamos a financiar este viaje. Solicita un crédito y da el primer paso a tu próximo destino.</p>
                <button type="button" className="primary-button" onClick={() => navigate("/credito")}><CreditCard size={15} aria-hidden="true" /> Solicitar crédito</button>
              </div>
            )}
          </section>
          <RegulatedNote />
        </div>

        <aside className="trip-rail">
          <div className="price-panel plan-panel card">
            <div className="plan-head">
              <h2>Planea tu viaje</h2>
              <small>{[trip.name, trip.durationDays ? `${trip.durationDays} días` : null].filter(Boolean).join(" · ")}</small>
            </div>
            <div className="plan-field">
              <span className="plan-label-row">
                <label htmlFor="plan-departure">Fecha de salida</label>
                {departures.length > 4 && <button type="button" className="link-button" onClick={showDepartures}>Ver calendario</button>}
              </span>
              <select id="plan-departure" className="standalone-input" value={departure ?? ""} onChange={(event) => setDeparture(event.target.value || null)} disabled={!departures.length} translate="no">
                {!departures.length && <option value="">Sin salidas disponibles</option>}
                {departureMonths.map((month) => (
                  <optgroup key={month.key} label={month.label.charAt(0).toUpperCase() + month.label.slice(1)}>
                    {month.days.map((day) => {
                      const iso = `${month.key}-${String(day).padStart(2, "0")}`;
                      return <option key={iso} value={iso}>{departureLabel(iso, true)}</option>;
                    })}
                  </optgroup>
                ))}
              </select>
              {plan.seasonHint && <span className="field-hint">{plan.seasonHint}</span>}
              {departures.length > 0 && <span className="field-hint">{trip.arrivalDays.some((day) => /diari/i.test(day)) ? "Salidas diarias" : trip.arrivalDays.length ? `Salidas en ${trip.arrivalDays.join(" y ").toLowerCase()}` : "Salidas fijas"}{trip.validUntil ? ` · tarifas vigentes hasta ${formatCalendarDate(trip.validUntil)}` : ""}</span>}
            </div>
            {plan.choices.filter((choice) => choice.kind === "season" && !choice.auto).map((choice) => <PlanSelect key={choice.index} choice={choice} onPick={pick} />)}
            <div className="plan-field">
              <label htmlFor="plan-room">Personas por habitación</label>
              <select id="plan-room" className="standalone-input" value={occupancy} onChange={(event) => setOccupancy(event.target.value as Occupancy)} disabled={occupancies.length < 2}>
                {occupancies.map((choice) => {
                  const capacity = OCCUPANCY_INFO[choice].capacity;
                  return <option key={choice} value={choice}>{OCCUPANCY_INFO[choice].long} · {capacity} {capacity === 1 ? "persona" : "personas"}</option>;
                })}
              </select>
            </div>
            {plan.choices.filter((choice) => choice.kind !== "season").map((choice) => <PlanSelect key={choice.index} choice={choice} onPick={pick} />)}

            <div className="plan-price">
              <div className="price-figure" translate="no"><strong>{rate ? amountWithCurrency(rate.amount, rate.currency) : "Consultar"}</strong></div>
              <small>Tarifa publicada por persona{plan.packageLabel ? ` · ${plan.packageLabel}` : ""}</small>
              {departure && <small><span translate="no">{formatCalendarDate(departure)}</span> · habitación {OCCUPANCY_INFO[occupancy].long.toLowerCase()}</small>}
              {rate && rate.supplement > 0 && <small>Incluye suplemento de salida de <span translate="no">{amountWithCurrency(rate.supplement, rate.currency)}</span></small>}
              {rate && OCCUPANCY_INFO[occupancy].capacity > 1 && <small className="plan-total">Habitación {OCCUPANCY_INFO[occupancy].long.toLowerCase()} · {OCCUPANCY_INFO[occupancy].capacity} personas: <strong translate="no">{amountWithCurrency(rate.amount * OCCUPANCY_INFO[occupancy].capacity, rate.currency)}</strong></small>}
              {!plan.packageLabel && <small className="price-scope">{content.pricing.price_scope || "Solo servicios terrestres · no incluye vuelos internacionales"}</small>}
            </div>
            <div className="plan-availability">
              <strong>Disponibilidad por confirmar</strong>
              <small>Tu asesor confirma espacios y la tarifa final en menos de 24 horas hábiles.</small>
              {extraAddOns > 0 && <small>{extraAddOns === 1 ? "1 suplemento se cotiza aparte" : `${extraAddOns} suplementos se cotizan aparte`} · <button type="button" className="link-button" onClick={() => revealPanel("prices")}>Ver tarifas</button></small>}
            </div>

            <div className="price-actions">
              <button type="button" className="primary-button solid full" onClick={() => openReserve("trip_page")} disabled={!departures.length}>Consultar disponibilidad</button>
              {!departures.length && (
                <>
                  <span className="field-hint">Sin salidas disponibles con {config.data?.minLeadDays ?? 28} días de anticipación. Te armamos una propuesta a la medida con tus fechas.</span>
                  <button type="button" className="secondary-button full" onClick={askCustomTrip}>Pedir propuesta a la medida</button>
                </>
              )}
              <button type="button" className="secondary-button full" onClick={openPlan}>Crear Apartado para este viaje</button>
              {loans.data?.enabled && <button type="button" className="text-link center" onClick={() => navigate("/credito")}><CreditCard size={14} aria-hidden="true" /> Solicitar crédito</button>}
            </div>
            <p className="wallet-line">Aparta con <span translate="no">{mxn(minPerPassenger).replace(".00", "")}</span> por pasajero desde tu Wallet Libre y paga el resto hasta 30 días antes de tu salida.</p>
          </div>
          <div className="card advisor-card">
            <strong>¿Dudas con las fechas o la visa?</strong>
            <p>Te acompañamos en cada etapa: un asesor revisa tu itinerario antes de reservar.</p>
            <button type="button" className="secondary-button small" onClick={() => (guest ? askForAccount("hablar con un asesor") : navigate("/viajes/a-la-medida"))}>Hablar con un asesor</button>
          </div>
        </aside>
      </div>

      <div className="trip-mobile-bar">
        <div>
          <span>Desde · por persona</span>
          <strong translate="no">{rate ? amountWithCurrency(rate.amount, rate.currency) : "Consultar"}</strong>
        </div>
        <button type="button" className="secondary-button small" onClick={openPlan}>Apartar</button>
        <button type="button" className="primary-button solid small" onClick={() => openReserve("mobile_bar")} disabled={!departures.length}>Reservar</button>
      </div>

      <ReservationModal
        trip={trip} open={reserveOpen} onOpenChange={setReserveOpen} entryPoint={reserveEntry}
        prefill={{ departureDate: departure, rooms: [occupancy], adults: OCCUPANCY_INFO[occupancy].capacity, children: 0, childAges: [], serviceCategory: rate?.season.service_category || null }}
        onSubmitted={submitted}
      />
      <PlanningModal
        trip={trip} open={planOpen} onOpenChange={setPlanOpen} minPerPassenger={minPerPassenger}
        onReserve={() => { setPlanOpen(false); openReserve("planning_modal"); }}
        onGoal={() => { setPlanOpen(false); setGoalOpen(true); }}
      />
      <TripGoalDialog trip={trip} wallet={wallet} open={goalOpen} onOpenChange={setGoalOpen} suggestedTarget={suggestedGoal} />

      <Modal open={photoIndex !== null} onOpenChange={(open) => { if (!open) setPhotoIndex(null); }} label="Galería de fotos">
        {photoIndex !== null && gallery[photoIndex] && (
          <div className="dialog-content wide gallery-viewer">
            <button type="button" className="dialog-close" onClick={() => setPhotoIndex(null)} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
            <figure>
              <img src={mediaVariant(gallery[photoIndex]!.url, 1280)} srcSet={slotSrcSet(gallery[photoIndex]!.url, "wide")} sizes="(max-width: 900px) 100vw, 1100px" alt={gallery[photoIndex]!.title ?? `${trip.name}, foto ${photoIndex + 1}`} />
              <figcaption>
                <strong>{gallery[photoIndex]!.title ?? trip.name}</strong>
                <span translate="no">{photoIndex + 1} / {gallery.length}</span>
              </figcaption>
            </figure>
            {gallery.length > 1 && (
              <div className="gallery-nav">
                <button type="button" className="secondary-button small" onClick={() => setPhotoIndex((photoIndex - 1 + gallery.length) % gallery.length)} aria-label="Foto anterior"><ChevronLeft size={16} aria-hidden="true" /> Anterior</button>
                <button type="button" className="secondary-button small" onClick={() => setPhotoIndex((photoIndex + 1) % gallery.length)} aria-label="Foto siguiente">Siguiente <ChevronRight size={16} aria-hidden="true" /></button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

/** One "Planea tu viaje" choice (season, hotel category, option or package) as a labelled select. */
function PlanSelect({ choice, onPick }: { choice: PlanChoice; onPick(index: number, value: string): void }) {
  const id = `plan-choice-${choice.index}`;
  return (
    <div className="plan-field">
      <label htmlFor={id}>{choice.label}</label>
      <select id={id} className="standalone-input" value={choice.value} onChange={(event) => onPick(choice.index, event.target.value)}>
        {choice.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="trip-stat"><span>{label}</span><strong>{value}</strong></div>;
}

function Panel({ id, title, hint, open, onToggle, children }: { id: string; title: string; hint: string; open: boolean; onToggle(): void; children: ReactNode }) {
  return (
    <div className={`detail-panel${open ? " open" : ""}`} id={`panel-${id}`}>
      <button type="button" className="detail-head" onClick={onToggle} aria-expanded={open} aria-controls={`panel-body-${id}`}>
        <span><strong>{title}</strong><small>{hint}</small></span>
        <span className="day-sign" aria-hidden="true">{open ? <Minus size={16} /> : <Plus size={16} />}</span>
      </button>
      {open && <div className="detail-body" id={`panel-body-${id}`}>{children}</div>}
    </div>
  );
}

function RatesTable({ content }: { content: TripContent }) {
  const occupancies = [...new Set(content.pricing.seasons.flatMap((season) => season.rates.map((rate) => rate.occupancy)))];
  return (
    <div className="table-wrap">
      <table className="trip-table">
        <thead><tr><th>Vigencia</th><th>Categoría</th>{occupancies.map((occ) => <th key={occ}>{OCCUPANCY_INFO[occ].short}</th>)}</tr></thead>
        <tbody>
          {content.pricing.seasons.map((season, i) => (
            <tr key={i}>
              <td translate="no">{season.valid_from ? formatCalendarDate(season.valid_from) : "—"}{season.valid_to ? ` – ${formatCalendarDate(season.valid_to)}` : ""}</td>
              <td>{season.service_category || "—"}</td>
              {occupancies.map((occ) => { const rate = season.rates.find((item) => item.occupancy === occ); return <td key={occ} translate="no">{rate?.amount !== null && rate?.amount !== undefined ? amountWithCurrency(rate.amount, rate.currency || content.pricing.currency) : "—"}</td>; })}
            </tr>
          ))}
        </tbody>
      </table>
      <small className="fine-print">Tarifa {content.pricing.price_per === "person" ? "por persona" : content.pricing.price_per} en {content.pricing.currency || "USD"}{content.pricing.minimum_passengers ? ` · mínimo ${content.pricing.minimum_passengers} pasajeros` : ""}.</small>
    </div>
  );
}
